import { debtorAccountNumber, debtorRecordKey } from './debtorRecords.js';

const STORAGE_KEY = 'demandjet.debtorProfiles';

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

function scopeKey(email) {
  return String(email || 'anon').trim().toLowerCase() || 'anon';
}

/**
 * Standalone debtor profiles (created without a demand yet).
 * Merged into debtor records alongside demand-aggregated debtors.
 */
export function listDebtorProfiles(email) {
  const all = readAll();
  const list = all[scopeKey(email)];
  return Array.isArray(list) ? list : [];
}

export function upsertDebtorProfile(email, profile) {
  const accountNumber = debtorAccountNumber(profile);
  const identity = {
    Debtor_Name: profile.Debtor_Name || profile.name || '',
    Debtor_Company: profile.Debtor_Company || profile.company || '',
    Debtor_State: profile.Debtor_State || profile.state || '',
    External_Account_Number: accountNumber,
  };
  const id = profile.id || debtorRecordKey(identity);
  if (!id || id === '||') {
    throw new Error('Company or contact name is required.');
  }

  const explicitNotesEntries = Array.isArray(profile.notesEntries)
    ? profile.notesEntries
    : Array.isArray(profile.Notes_Entries)
      ? profile.Notes_Entries
      : null;
  const explicitNotes =
    profile.notes != null
      ? String(profile.notes)
      : profile.Notes != null
        ? String(profile.Notes)
        : null;

  const next = {
    id,
    name: String(identity.Debtor_Name || identity.Debtor_Company || '').trim(),
    company: String(identity.Debtor_Company || identity.Debtor_Name || '').trim(),
    state: String(identity.Debtor_State || '').trim(),
    country: profile.Debtor_Country || profile.country || 'US',
    street: profile.Debtor_Street || profile.street || '',
    city: profile.Debtor_City || profile.city || '',
    postalCode: profile.Debtor_Postal_Code || profile.postalCode || '',
    email: profile.Debtor_Email || profile.email || '',
    accountNumber,
    createdAt: profile.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const all = readAll();
  const key = scopeKey(email);
  const list = Array.isArray(all[key]) ? [...all[key]] : [];
  const idx = list.findIndex((item) => String(item.id) === String(id));
  // If identity key changed, remove old id when provided separately.
  if (profile.previousId && profile.previousId !== id) {
    const oldIdx = list.findIndex((item) => String(item.id) === String(profile.previousId));
    if (oldIdx >= 0) list.splice(oldIdx, 1);
  }
  const prior =
    idx >= 0 ? list[idx] : list.find((item) => String(item.id) === String(profile.previousId));
  const notesEntries =
    explicitNotesEntries != null
      ? explicitNotesEntries
      : Array.isArray(prior?.notesEntries)
        ? prior.notesEntries
        : [];
  const notes =
    explicitNotes != null
      ? explicitNotes
      : notesEntries.length
        ? notesEntries.map((n) => n.body || n.text || '').filter(Boolean).join('\n\n')
        : prior?.notes || '';
  const saved = { ...next, notes, notesEntries };
  if (idx >= 0) list[idx] = { ...list[idx], ...saved };
  else list.push(saved);
  all[key] = list;
  writeAll(all);
  return saved;
}

export function removeDebtorProfile(email, debtorId) {
  const all = readAll();
  const key = scopeKey(email);
  const list = Array.isArray(all[key]) ? all[key] : [];
  all[key] = list.filter((item) => String(item.id) !== String(debtorId));
  writeAll(all);
}

export function profileToDebtorRecord(profile) {
  return {
    id: profile.id,
    name: profile.name || '',
    company: profile.company || '',
    state: profile.state || '',
    country: profile.country || 'US',
    street: profile.street || '',
    city: profile.city || '',
    postalCode: profile.postalCode || '',
    email: profile.email || '',
    accountNumber: debtorAccountNumber(profile),
    notes: profile.notes || '',
    notesEntries: Array.isArray(profile.notesEntries) ? profile.notesEntries : [],
    matters: [],
    totalOwed: 0,
    openCount: 0,
    recovered: 0,
    primaryMatter: null,
    isProfileOnly: true,
  };
}
